import type { ClientProfile, ClientStatus } from './types'
import { MOCK_CLIENTS as BASE_CLIENTS } from './mockClients.generated'

const STORAGE_KEY = 'madc.addedClients'

function readAdded(): ClientProfile[] {
  try {
    const raw = sessionStorage.getItem(STORAGE_KEY)
    if (!raw) return []
    const parsed = JSON.parse(raw) as ClientProfile[]
    return Array.isArray(parsed) ? parsed : []
  } catch {
    return []
  }
}

function writeAdded(list: ClientProfile[]) {
  try {
    sessionStorage.setItem(STORAGE_KEY, JSON.stringify(list))
  } catch {
    /* ignore quota */
  }
}

/** Full roster = newly added clients (session) + captured list. */
export function getAllClients(): ClientProfile[] {
  return [...readAdded(), ...BASE_CLIENTS]
}

export function getClientById(id: string): ClientProfile | undefined {
  return getAllClients().find((c) => c.id === id)
}

export function displayName(c: Pick<ClientProfile, 'firstName' | 'lastName' | 'middleName'>): string {
  const mid = c.middleName ? ` ${c.middleName}` : ''
  return `${c.lastName}, ${c.firstName}${mid}`.trim()
}

export function statusCounts() {
  const all = getAllClients()
  const count = (s: ClientStatus) => all.filter((c) => c.status === s).length
  return {
    all: all.length,
    approved: count('Approved'),
    visitors: count('Visitor'),
    discharged: count('Discharged'),
    hold: count('Hold'),
    potential: count('Potential'),
  }
}

export type NewClientInput = {
  firstName: string
  lastName: string
  middleName?: string
  phoneHome?: string
  phoneCell?: string
  dob: string
  gender?: string
  status: ClientStatus
  email?: string
  admissionDate?: string
  address?: string
  address2?: string
  city?: string
  state?: string
  zip?: string
  ssn?: string
  careManager?: string
  program?: string
  medicaidId?: string
  medicareId?: string
  maritalStatus?: string
  primaryLanguage?: string
  secondaryLanguage?: string
  race?: string
  ethnicity?: string
  religion?: string
  comments?: string
  referralDate?: string
  referralSource?: string
}

export function addClient(input: NewClientInput): ClientProfile {
  const id = String(90000 + Math.floor(Math.random() * 9000))
  const client: ClientProfile = {
    id,
    firstName: input.firstName.trim(),
    lastName: input.lastName.trim(),
    middleName: input.middleName?.trim() || undefined,
    phoneHome: input.phoneHome?.trim() || undefined,
    phoneCell: input.phoneCell?.trim() || undefined,
    dob: input.dob.trim(),
    gender: input.gender,
    status: input.status,
    email: input.email?.trim() || undefined,
    admissionDate: input.admissionDate?.trim() || undefined,
    address: [input.address, input.address2, input.city, input.state, input.zip]
      .filter(Boolean)
      .join(', ') || undefined,
    careManager: input.careManager?.trim() || undefined,
    program: input.program?.trim() || undefined,
    medicaidId: input.medicaidId?.trim() || undefined,
    medicareId: input.medicareId?.trim() || undefined,
    race: input.race || input.ethnicity,
    ssnLast4: input.ssn ? input.ssn.replace(/\D/g, '').slice(-4) : undefined,
  }
  const next = [client, ...readAdded()]
  writeAdded(next)
  return client
}
